<script lang="ts">
  import { onMount } from 'svelte';
  import { signup, login } from '../../lib/auth.svelte';
  import { db, auth } from '../../lib/firebase';
  import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
  import { sendPasswordResetEmail } from 'firebase/auth';
  import { getQueryParams } from '../../lib/router.svelte';

  // Types
  type Role = 'accountAdmin' | 'businessAdmin' | 'staff' | 'client' | 'freight' | 'account';

  // Map portal roles to Flutter userType values
  const ROLE_TO_USERTYPE: Record<Role, string> = {
    'accountAdmin': 'account_admin',
    'businessAdmin': 'business_admin',
    'staff': 'staff_user',
    'client': 'client_user',
    'freight': 'freight_user',
    'account': 'account_user',
  };

  // Role display labels
  const ROLE_LABELS: Record<Role, string> = {
    'accountAdmin': 'Account Admin',
    'businessAdmin': 'Business Admin',
    'staff': 'Staff',
    'client': 'Client',
    'freight': 'Freight',
    'account': 'Account',
  };

  type Invitation = {
    id: string;
    email: string;
    name: string;
    role: Role;
    userType?: string;
    invitedBy: string;
    inviterEmail: string;
    businessName: string;
    businessId?: string;
    locationId?: string;
    locationName?: string;
    allowedLocationIds?: string[];
    allowedModules?: string[];
    createdAt: string;
    expiresAt: string;
    status: 'pending' | 'accepted' | 'expired' | 'revoked';
  };

  // State
  let inviteId = $state<string | null>(null);
  let invitation = $state<Invitation | null>(null);
  let loadingInvitation = $state(true);
  let invitationError = $state('');

  // Form data
  let firstName = $state('');
  let lastName = $state('');
  let email = $state('');
  let phone = $state('');
  let password = $state('');
  let confirmPassword = $state('');

  // Form state
  let isSubmitting = $state(false);
  let errorMessage = $state('');
  let isSuccess = $state(false);
  let isExistingUser = $state(false);
  let passwordResetSent = $state(false);

  onMount(() => {
    const params = getQueryParams();
    if (params.invite) {
      inviteId = params.invite;
      loadInvitation(params.invite);
    } else {
      loadingInvitation = false;
      invitationError = 'No invitation ID provided';
    }
  });

  async function loadInvitation(id: string) {
    loadingInvitation = true;
    invitationError = '';

    try {
      const inviteDoc = await getDoc(doc(db, 'invitations', id));

      if (!inviteDoc.exists()) {
        invitationError = 'Invitation not found';
        return;
      }

      const data = inviteDoc.data() as Omit<Invitation, 'id'>;

      if (data.status !== 'pending') {
        invitationError =
          data.status === 'accepted'
            ? 'This invitation has already been accepted'
            : data.status === 'revoked'
              ? 'This invitation has been revoked'
              : 'This invitation has expired';
        return;
      }

      if (new Date(data.expiresAt) < new Date()) {
        invitationError = 'This invitation has expired';
        return;
      }

      invitation = { id, ...data };
      email = data.email;
      firstName = data.name.split(' ')[0] || '';
      lastName = data.name.split(' ').slice(1).join(' ') || '';
    } catch (err) {
      console.error('Error loading invitation:', err);
      invitationError = 'Failed to load invitation';
    } finally {
      loadingInvitation = false;
    }
  }

  function validateForm(): string | null {
    if (!firstName.trim()) return 'First name is required';
    if (!lastName.trim()) return 'Last name is required';
    if (!email.trim()) return 'Email is required';
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (password !== confirmPassword) return 'Passwords do not match';
    return null;
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      errorMessage = validationError;
      return;
    }

    if (!invitation) {
      errorMessage = 'No invitation loaded';
      return;
    }

    isSubmitting = true;
    errorMessage = '';

    try {
      const now = new Date().toISOString();

      // Create account with password
      const result = await signup(email, password);

      if (!result.success) {
        // Check if user already exists
        if (result.error?.includes('email-already-in-use')) {
          isExistingUser = true;
          errorMessage = '';
          // Send password reset email — helps both backend-created users (no password)
          // and genuine existing users who may have forgotten their password.
          // Note: sendPasswordResetEmail succeeds for all valid users regardless of
          // whether they already have a password set, so we can't use it to distinguish
          // backend-created from genuine existing users.
          try {
            await sendPasswordResetEmail(auth, email);
            passwordResetSent = true;
          } catch (resetErr) {
            console.log('Password reset email not sent:', resetErr);
          }
          return;
        }
        throw new Error(result.error || 'Failed to create account');
      }

      const user = result.user;
      if (!user) {
        throw new Error('No user returned from signup');
      }

      // Build user document
      const userData: Record<string, any> = {
        email: email.toLowerCase().trim(),
        displayName: `${firstName.trim()} ${lastName.trim()}`,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        role: invitation.role,
        userType: invitation.userType || ROLE_TO_USERTYPE[invitation.role],
        clientId: invitation.businessId || invitation.invitedBy,
        businessId: invitation.businessId || invitation.invitedBy,
        businessName: invitation.businessName,
        teamOwnerId: invitation.invitedBy,
        isActive: true,
        approvalStatus: 'approved',
        claimsSet: false,
        invitedBy: invitation.invitedBy,
        createdAt: now,
        updatedAt: now,
      };

      // Add location information if present
      if (invitation.locationId) {
        userData.locationId = invitation.locationId;
      }
      if (invitation.locationName) {
        userData.locationName = invitation.locationName;
      }

      // Add location restrictions if present
      if (invitation.allowedLocationIds && invitation.allowedLocationIds.length > 0) {
        userData.allowedLocationIds = invitation.allowedLocationIds;
      }

      // Add module access if present
      if (invitation.allowedModules && invitation.allowedModules.length > 0) {
        userData.allowedModules = invitation.allowedModules;
      }

      await setDoc(doc(db, 'users', user.uid), userData, { merge: true });

      // Mark invitation as accepted
      await updateDoc(doc(db, 'invitations', invitation.id), {
        status: 'accepted',
        acceptedAt: now,
        acceptedBy: user.uid,
      });

      // Show success
      isSuccess = true;
    } catch (err: any) {
      console.error('Error creating account:', err);
      errorMessage = err.message || 'Failed to create account';
    } finally {
      isSubmitting = false;
    }
  }

  async function linkBusinessToUser(userId: string) {
    if (!invitation) return;

    const now = new Date().toISOString();
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const existingData = userDoc.exists() ? userDoc.data() : {};

    // Merge business IDs
    const existingBusinessIds: string[] = existingData.allowedBusinessIds || [];
    const newBusinessId = invitation.businessId || invitation.invitedBy;
    const mergedBusinessIds = [...new Set([...existingBusinessIds, newBusinessId])];

    // Merge modules
    const existingModules: string[] = existingData.allowedModules || [];
    const mergedModules = [...new Set([...existingModules, ...(invitation.allowedModules || [])])];

    // Merge location IDs
    const existingLocationIds: string[] = existingData.allowedLocationIds || [];
    const mergedLocationIds = [...new Set([...existingLocationIds, ...(invitation.allowedLocationIds || [])])];

    // Update user document with merged permissions
    await updateDoc(userRef, {
      allowedBusinessIds: mergedBusinessIds,
      allowedModules: mergedModules,
      allowedLocationIds: mergedLocationIds,
      claimsSet: false,
      updatedAt: now,
    });

    // Mark invitation as accepted
    await updateDoc(doc(db, 'invitations', invitation.id), {
      status: 'accepted',
      acceptedAt: now,
      acceptedBy: userId,
      linkedToExistingUser: true,
    });
  }

  async function createUserDocFromInvitation(userId: string) {
    if (!invitation) return;
    const now = new Date().toISOString();
    const userData: Record<string, any> = {
      email: email.toLowerCase().trim(),
      displayName: `${firstName.trim()} ${lastName.trim()}`,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      role: invitation.role,
      userType: invitation.userType || ROLE_TO_USERTYPE[invitation.role],
      clientId: invitation.businessId || invitation.invitedBy,
      businessId: invitation.businessId || invitation.invitedBy,
      businessName: invitation.businessName,
      teamOwnerId: invitation.invitedBy,
      isActive: true,
      approvalStatus: 'approved',
      claimsSet: false,
      invitedBy: invitation.invitedBy,
      createdAt: now,
      updatedAt: now,
    };
    if (invitation.locationId) userData.locationId = invitation.locationId;
    if (invitation.locationName) userData.locationName = invitation.locationName;
    if (invitation.allowedLocationIds && invitation.allowedLocationIds.length > 0) {
      userData.allowedLocationIds = invitation.allowedLocationIds;
    }
    if (invitation.allowedModules && invitation.allowedModules.length > 0) {
      userData.allowedModules = invitation.allowedModules;
    }
    await setDoc(doc(db, 'users', userId), userData, { merge: true });
    await updateDoc(doc(db, 'invitations', invitation.id), {
      status: 'accepted',
      acceptedAt: now,
      acceptedBy: userId,
    });
  }

  async function handleExistingUserSignIn(e: Event) {
    e.preventDefault();
    isSubmitting = true;
    errorMessage = '';

    try {
      const result = await login(email, password);
      if (!result.success) {
        throw new Error(result.error || 'Invalid password');
      }

      if (!result.user) {
        throw new Error('No user returned from login');
      }

      // Check if Firestore user doc exists
      const userRef = doc(db, 'users', result.user.uid);
      const userDocSnap = await getDoc(userRef);

      if (userDocSnap.exists()) {
        // Existing user with doc — just link the new business
        await linkBusinessToUser(result.user.uid);
      } else {
        // Backend-created user with no Firestore doc — create full doc
        await createUserDocFromInvitation(result.user.uid);
      }

      isSuccess = true;
    } catch (err: any) {
      console.error('Error signing in existing user:', err);
      errorMessage = err.message || 'Failed to sign in';
    } finally {
      isSubmitting = false;
    }
  }

  function goToLogin() {
    window.location.hash = 'login';
  }

  function goToSignup() {
    window.location.hash = 'signup';
  }
</script>

<div class="join-container">
  {#if loadingInvitation}
    <!-- Loading -->
    <div class="card loading-card">
      <div class="spinner-large"></div>
      <p>Loading invitation...</p>
    </div>
  {:else if invitationError}
    <!-- Error -->
    <div class="card error-card">
      <div class="icon error">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" stroke-linecap="round" />
          <line x1="9" y1="9" x2="15" y2="15" stroke-linecap="round" />
        </svg>
      </div>
      <h1>Invitation Error</h1>
      <p>{invitationError}</p>
      <div class="actions">
        <button class="btn-primary" onclick={goToSignup}>Create New Account</button>
        <button class="btn-secondary" onclick={goToLogin}>Sign In Instead</button>
      </div>
    </div>
  {:else if isSuccess && invitation}
    <!-- Success -->
    <div class="card success-card">
      <div class="icon success">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M8 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      {#if isExistingUser}
        <h1>{invitation.businessName} Added!</h1>
        <p>You now have access to <strong>{invitation.businessName}</strong>. Switch businesses from your dashboard.</p>
      {:else}
        <h1>Welcome to {invitation.businessName}!</h1>
        <p>Your account has been created successfully. You can now sign in to access EQUOS.</p>
      {/if}
      <div class="role-badge">
        <span class="label">Your role:</span>
        <span class="value">{ROLE_LABELS[invitation.role] || invitation.role}</span>
      </div>
      <button class="btn-primary" onclick={() => window.location.hash = isExistingUser ? 'dashboard' : 'login'}>
        {isExistingUser ? 'Go to Dashboard' : 'Sign In'}
      </button>
      <p class="helper">
        Questions? Contact your administrator or <a href="mailto:support@equosnine.com">support@equosnine.com</a>
      </p>
    </div>
  {:else if isExistingUser && invitation}
    <!-- Existing User Sign-in -->
    <div class="join-shell">
      <header class="join-header">
        <a href="/" class="logo">EQUOS9</a>
        <p class="eyebrow">Link Account</p>
        <h1>Sign In to Continue</h1>
        <p class="lede">
            An account with <strong>{email}</strong> already exists.
            Sign in below to join <strong>{invitation.businessName}</strong>.
            {#if passwordResetSent}
                A password reset link has also been sent to your email.
            {/if}
        </p>
      </header>

      <div class="invite-card">
        <div class="invite-info">
          <div class="invite-row">
            <span class="invite-label">Adding business</span>
            <span class="invite-value">{invitation.businessName}</span>
          </div>
          <div class="invite-row">
            <span class="invite-label">Your role</span>
            <span class="invite-value">{ROLE_LABELS[invitation.role]}</span>
          </div>
        </div>
      </div>

      {#if errorMessage}
        <div class="error-banner">
          <span>!</span>
          {errorMessage}
        </div>
      {/if}

      <form class="join-form" onsubmit={handleExistingUserSignIn}>
        <div class="form-group full-width">
          <label for="email">Email</label>
          <input type="email" id="email" value={email} disabled />
        </div>
        <div class="form-group full-width" style="margin-top: 1rem;">
          <label for="password">Password</label>
          <input
            type="password"
            id="password"
            bind:value={password}
            placeholder="Enter your existing password"
            disabled={isSubmitting}
          />
        </div>

        <button type="submit" class="btn-primary full-width" style="margin-top: 1.5rem;" disabled={isSubmitting}>
          {#if isSubmitting}
            <span class="spinner"></span>
            Linking account...
          {:else}
            Sign In & Link Business
          {/if}
        </button>

        <div class="links">
          <span>Forgot password?</span>
          <a href="#reset-password">Reset it here</a>
        </div>
        <div class="links" style="margin-top: 0.75rem;">
            <button type="button" class="link-button" onclick={async () => {
                try {
                    await sendPasswordResetEmail(auth, email);
                    passwordResetSent = true;
                } catch (e) { console.error(e); }
            }}>
                {passwordResetSent ? 'Password link sent!' : 'Forgot password? Send reset link'}
            </button>
        </div>
      </form>
    </div>
  {:else if invitation}
    <!-- Form -->
    <div class="join-shell">
      <header class="join-header">
        <a href="/" class="logo">EQUOS9</a>
        <p class="eyebrow">Accept Invitation</p>
        <h1>Join {invitation.businessName}</h1>
        <p class="lede">
          Complete your account to get started as <strong>{ROLE_LABELS[invitation.role] || invitation.role}</strong>
        </p>
      </header>

      <!-- Invitation details -->
      <div class="invite-card">
        <div class="invite-info">
          <div class="invite-row">
            <span class="invite-label">Invited by</span>
            <span class="invite-value">{invitation.inviterEmail}</span>
          </div>
          <div class="invite-row">
            <span class="invite-label">Role</span>
            <span class="invite-value">{ROLE_LABELS[invitation.role] || invitation.role}</span>
          </div>
          {#if invitation.locationName}
            <div class="invite-row">
              <span class="invite-label">Location</span>
              <span class="invite-value">{invitation.locationName}</span>
            </div>
          {/if}
        </div>
      </div>

      {#if errorMessage}
        <div class="error-banner">
          <span>!</span>
          {errorMessage}
        </div>
      {/if}

      <form class="join-form" onsubmit={handleSubmit}>
        <div class="form-grid">
          <div class="form-group">
            <label for="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              bind:value={firstName}
              placeholder="John"
              disabled={isSubmitting}
              required
            />
          </div>
          <div class="form-group">
            <label for="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              bind:value={lastName}
              placeholder="Smith"
              disabled={isSubmitting}
              required
            />
          </div>
          <div class="form-group full-width">
            <label for="email">Email</label>
            <input
              type="email"
              id="email"
              bind:value={email}
              disabled
            />
            <span class="field-hint">Email from invitation</span>
          </div>
          <div class="form-group full-width">
            <label for="phone">Phone (optional)</label>
            <input
              type="tel"
              id="phone"
              bind:value={phone}
              placeholder="0412 345 678"
              disabled={isSubmitting}
            />
          </div>
          <div class="form-group">
            <label for="password">Password</label>
            <input
              type="password"
              id="password"
              bind:value={password}
              placeholder="Minimum 8 characters"
              minlength="8"
              disabled={isSubmitting}
              required
            />
          </div>
          <div class="form-group">
            <label for="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              bind:value={confirmPassword}
              placeholder="Re-enter your password"
              disabled={isSubmitting}
              required
            />
          </div>
        </div>

        <button type="submit" class="btn-primary full-width" disabled={isSubmitting}>
          {#if isSubmitting}
            <span class="spinner"></span>
            Creating account...
          {:else}
            Create Account
          {/if}
        </button>

        <div class="links">
          <span>Already have an account?</span>
          <button type="button" class="link-button" onclick={goToLogin}>Sign in</button>
        </div>
      </form>
    </div>
  {/if}
</div>

<style>
  .join-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 2rem 1rem;
  }

  .join-shell {
    max-width: 480px;
    width: 100%;
  }

  /* Header */
  .join-header {
    text-align: center;
    margin-bottom: 1.5rem;
    color: white;
  }

  .logo {
    display: inline-block;
    font-size: 1.5rem;
    font-weight: bold;
    color: white;
    text-decoration: none;
    margin-bottom: 1rem;
  }

  .eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.2em;
    opacity: 0.8;
    font-size: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .join-header h1 {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
  }

  .join-header .lede {
    opacity: 0.9;
    font-size: 1rem;
  }

  .join-header .lede strong {
    color: white;
    font-weight: 600;
  }

  /* Cards */
  .card {
    background: white;
    border-radius: 12px;
    padding: 3rem 2rem;
    text-align: center;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    max-width: 480px;
    width: 100%;
  }

  .loading-card p {
    color: #666;
    margin-top: 1rem;
  }

  .spinner-large {
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
  }

  .icon svg {
    width: 32px;
    height: 32px;
  }

  .icon.error {
    background: #fef2f2;
    color: #dc2626;
  }

  .icon.success {
    background: #f0fdf4;
    color: #16a34a;
  }

  .error-card h1,
  .success-card h1 {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
    color: #111;
  }

  .error-card p,
  .success-card p {
    color: #666;
    margin-bottom: 1.5rem;
  }

  .actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .role-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: #f3f4f6;
    border-radius: 999px;
    margin-bottom: 1.5rem;
  }

  .role-badge .label {
    color: #666;
    font-size: 0.875rem;
  }

  .role-badge .value {
    font-weight: 600;
    color: #667eea;
  }

  /* Invite card */
  .invite-card {
    background: white;
    border-radius: 12px;
    padding: 1.25rem;
    margin-bottom: 1rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  .invite-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .invite-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .invite-label {
    color: #666;
    font-size: 0.875rem;
  }

  .invite-value {
    font-weight: 500;
    color: #111;
  }

  /* Form */
  .join-form {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  }

  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .form-group.full-width {
    grid-column: 1 / -1;
  }

  .form-group label {
    font-weight: 500;
    font-size: 0.875rem;
    color: #374151;
  }

  .form-group input {
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .form-group input:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  .form-group input:disabled {
    background: #f9fafb;
    color: #6b7280;
  }

  .field-hint {
    font-size: 0.75rem;
    color: #6b7280;
  }

  /* Error banner */
  .error-banner {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: #dc2626;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }

  .error-banner span {
    font-weight: bold;
  }

  /* Buttons */
  .btn-primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.875rem 1.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  .btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .btn-primary.full-width {
    width: 100%;
  }

  .btn-secondary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.875rem 1.5rem;
    background: white;
    color: #667eea;
    border: 1px solid #667eea;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-secondary:hover {
    background: #f9fafb;
  }

  .spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  /* Links */
  .links {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1.25rem;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .link-button {
    background: none;
    border: none;
    color: #667eea;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
  }

  .link-button:hover {
    color: #5563c1;
  }

  .helper {
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
    margin-top: 1rem;
  }

  .helper a {
    color: #667eea;
  }

  /* Responsive */
  @media (max-width: 480px) {
    .form-grid {
      grid-template-columns: 1fr;
    }

    .form-group.full-width {
      grid-column: 1;
    }

    .card {
      padding: 2rem 1.5rem;
    }
  }
</style>
